import { type VariantProps, cva } from "class-variance-authority";

import { type Accessor, type JSX, Show, splitProps } from "solid-js";

import { getFileUrl } from "@/lib/resFolder";
import { cn } from "@/lib/tailwindcssTool";

export interface ButtonProps
  extends JSX.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof String> {
  loading?: boolean | Accessor<boolean>;
  disabled?: boolean;
  variants?: "default" | "primary" | "warning";
  onClick?: (e: MouseEvent) => void | Promise<unknown>;
}

export function Button(props: ButtonProps) {
  const [local, variantProps, others] = splitProps(
    props,
    ["class", "loading", "onClick"],
    ["variant", "size"],
  );

  const isLoading = () => {
    const loading = local.loading;
    return typeof loading === "function" ? loading() : !!loading;
  };

  const handleClick = (e: MouseEvent) => {
    if (isLoading() || props.disabled) return;

    if (local.onClick) {
      local.onClick(e);
    }
  };

  const initClass = () => {
    let className =
      "inline-flex items-center justify-center whitespace-nowrap text-[14px] font-[700] clickable focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none";
    if (variantProps.variant == "primary") {
      className +=
        " bg-[var(--colors-btn-brand-primary)] text-[var(--colors-text-brand)] border border-solid border-[var(--colors-btn-border-default)]";
    } else if (variantProps.variant == "warning") {
      className += " text-general-butzi bg-general-highbut";
    } else {
      className += " text-up-signin-zi bg-up-signin-but";
    }
    if (variantProps.size == "lg") {
      className += " px-[32px]";
    } else if (variantProps.size == "sm") {
      className += " px-[12px]";
    } else {
      className += " px-[18px]";
    }
    return className;
  };

  return (
    <button
      class={cn("h-[48px] rounded-[12px] px-[16px]", initClass(), local.class)}
      disabled={isLoading() || props.disabled}
      onClick={handleClick}
      {...others}
    >
      <Show when={isLoading()} fallback={props.children}>
        <img
          src={getFileUrl("btn_loading.gif?20250807", "common/public")}
          alt="loading"
          class="h-4/5 opacity-80"
        />
      </Show>
    </button>
  );
}
